/* eslint-disable @typescript-eslint/no-unsafe-call, promise/prefer-await-to-callbacks, no-shadow, @typescript-eslint/no-explicit-any, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps -- MobX store requires dynamic typing */

import { createContext, useContext, useEffect, useState } from 'react';

import type { IRootModel } from 'watchtower-ui/models/Root';
import { RootModel } from 'watchtower-ui/models/Root';
import { cleanupHooks, setupHooks } from 'watchtower-ui/models/store/hooks';
import { setupRootStore } from 'watchtower-ui/models/store/setup-root-store';

/**
 * Create the initial (empty) global RootStore instance here.
 *
 * Later, it will be rehydrated in app.tsx with the setupRootStore function.
 *
 * If your RootStore requires specific properties to be instantiated,
 * you can do so here.
 *
 * If your RootStore has a _ton_ of sub-stores and properties (the tree is
 * very large), you may want to use a different strategy than immediately
 * instantiating it, although that should be rare.
 */
const rootStore = RootModel.create({});

/**
 * The RootStoreContext provides a way to access
 * the RootStore in any screen or component.
 */
const RootStoreContext = createContext<IRootModel>(rootStore);

/**
 * A hook that screens and other components can use to gain access to
 * our stores:
 *
 * const rootStore = useStores()
 *
 * or:
 *
 * const { someStore, someOtherStore } = useStores()
 */
export const useStores = () => useContext(RootStoreContext);

/**
 * Used only in the app.tsx file, this hook sets up the RootStore
 * and then rehydrates it. It connects everything with Reactotron
 * and then lets the app know that everything is ready to go.
 */
export const useInitialRootStore = (callback: () => void | Promise<void>) => {
  const rootStore = useStores();
  const [rehydrated, setRehydrated] = useState(false);

  // Kick off initial async loading actions, like loading fonts and rehydrating RootStore
  useEffect(() => {
    // bind hooks
    setupHooks(rootStore);

    let _unsubscribe: any;
    (async () => {
      // set up the RootStore (returns the state restored from AsyncStorage)
      const { unsubscribe } = await setupRootStore(rootStore);
      _unsubscribe = unsubscribe;

      // let the app know we've finished rehydrating
      setRehydrated(true);

      // invoke the callback, if provided
      if (callback) callback();
    })();

    return () => {
      // unbind hooks
      cleanupHooks(rootStore);

      // cleanup
      if (_unsubscribe) _unsubscribe();
    };
  }, []);

  return { rootStore, rehydrated };
};
